import React, { useState, useEffect } from 'react';
import CustomInput from '../../components/customInput/CustomInput';
import CustomSelect from '../../components/customSelect/CustomSelect';
import Button from '../../components/button';
import './CreateUser.css';
import { useDispatch, useSelector } from 'react-redux';
import { registerUser, fetchRoles } from '../../redux/createUserSlice';
import { updateUser, } from '../../redux/userSlice';

import { isEmpty, isEmailValid } from '../../utils/commonUtil';
import { ERROR_INPUT_REQUIRED, ERROR_EMAIL_IS_INVALID, ERROR_PASSWORD_NOT_MATCH } from '../../utils/constant';
import { EDIT_MODEL_CREATE, EDIT_MODEL_VIEW, EDIT_MODEL_EDIT } from '../../utils/constant';

import CommonToast from '../../components/toast/CommonToast';

function CreateUser({ onCancel, onUserCreated, onUserUpdated, editMode, user }) {

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    role: '',
    password: '',
    confirmPassword: ''
  });

  const [formError, setFormError] = useState({
    username: undefined,
    email: undefined,
    role: undefined,
    password: undefined,
    confirmPassword: undefined
  });

  const dispatch = useDispatch();
  const { roles, loading, error } = useSelector((state) => state.createUser);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    dispatch(fetchRoles());
  }, [dispatch]);


  useEffect(() => {
    if (user) {
      const newData = {
        username: user.login,
        email: user.email,
        role: user.roleId.toString(),
        password: '',
        confirmPassword: ''
      }
      setFormData(newData);
    }
  }, [user]);

  const roleOptions = roles.map((role) => ({
    value: role.roleId.toString(),
    label: role.roleName
  }));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));

    setFormError(prevError => ({
      ...prevError,
      [name]: undefined
    }));
  };

  const handleSubmit = () => {

    //validation
    const newError = {
      username: undefined,
      email: undefined,
      role: undefined,
      password: undefined,
      confirmPassword: undefined
    };

    newError.username = isEmpty(formData.username) ? ERROR_INPUT_REQUIRED : undefined;
    newError.email = isEmpty(formData.email) ? ERROR_INPUT_REQUIRED : undefined;
    newError.role = isEmpty(formData.role) ? ERROR_INPUT_REQUIRED : undefined;

    if (editMode === EDIT_MODEL_CREATE) {
      newError.password = isEmpty(formData.password) ? ERROR_INPUT_REQUIRED : undefined;
      newError.confirmPassword = isEmpty(formData.confirmPassword) ? ERROR_INPUT_REQUIRED : undefined;
    }

    if (formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword) {
      newError.confirmPassword = ERROR_PASSWORD_NOT_MATCH;
    }

    if (formData.email && !isEmailValid(formData.email)) {
      newError.email = ERROR_EMAIL_IS_INVALID;
    }

    setFormError(newError);

    if (newError.username || newError.email || newError.role ||
      newError.password || newError.confirmPassword) {
      //don't call api if have validation error
      return;
    }

    //call api
    if (editMode === EDIT_MODEL_CREATE) {
      handleCreateSubmit();
    } else if (editMode === EDIT_MODEL_EDIT) {
      handleEditSubmit();
    }
  };

  const handleCreateSubmit = () => {
    const payload = {
      roleId: parseInt(formData.role),
      login: formData.username,
      email: formData.email,
      password: formData.password,
      confirmPassword: formData.confirmPassword
    };
    dispatch(registerUser(payload))
      .unwrap()
      .then(() => {
        setToastMessage('User created successfully');
        setIsError(false);
        setShowToast(true);
        if (onUserCreated) {
          onUserCreated();
        }
      })
      .catch((error) => {
        const errorMessage = error.message;
        setToastMessage(errorMessage);
        setIsError(true);
        setShowToast(true);
      });
  };

  const handleEditSubmit = () => {
    const payload = {
      roleId: parseInt(formData.role),
      login: formData.username,
      displayName: user.login,
      email: formData.email,
      password: formData.password,
      confirmPassword: formData.confirmPassword,
      isPasswordChange: false,
      userId: user.userId,
      isOtpVerified: "True"
    };

    payload.isPasswordChange = (formData.password && formData.confirmPassword) ? true : false;

    dispatch(updateUser(payload))
      .unwrap()
      .then(() => {
        setToastMessage('User updated successfully');
        setIsError(false);
        setShowToast(true);
        if (onUserUpdated) {
          setTimeout(() => onUserUpdated(), 1000);
        }
      })
      .catch((error) => {
        const errorMessage = error.message;
        setToastMessage(errorMessage);
        setIsError(true);
        setShowToast(true);
      });
  };

  const showTitle = () => {
    let title = '';
    switch (editMode) {
      case EDIT_MODEL_CREATE:
        title = 'Create User';
        break;
      case EDIT_MODEL_EDIT:
        title = 'Edit User';
        break;
      case EDIT_MODEL_VIEW:
        title = 'View User';
        break;
      default:
        break;
    }
    return title;
  };

  return (
    <div className='create-user-container'>
      <div style={{ padding: '50px' }}>
        <div style={{ fontSize: '24px' }}>{showTitle()}</div>
        <div style={{ marginTop: '30px' }}>
          <div className="create-user-form-row">
            <label>Username</label>
            <div className="input-wrapper">
              <CustomInput
                placeholder="Enter a username"
                name="username"
                error={formError.username}
                value={formData.username}
                onChange={handleInputChange}
                style={{ width: '240px', fontSize: '14px' }}
                disabled={editMode !== EDIT_MODEL_CREATE}
              />
              <span className="username-note">Note: Username must be at least 3 characters. Only alphanumeric characters are allowed.</span>
            </div>
          </div>
          <div className="create-user-form-row">
            <label>Email</label>
            <div className="input-wrapper">
              <CustomInput
                placeholder="Enter your email"
                name="email"
                error={formError.email}
                value={formData.email}
                onChange={handleInputChange}
                style={{ width: '240px', fontSize: '14px' }}
                disabled={editMode == EDIT_MODEL_VIEW}
              />
            </div>
          </div>
          <div className="create-user-form-row">
            <label>Role</label>
            <div className="input-wrapper">
              <CustomSelect
                option={roleOptions}
                onChange={(e) => handleInputChange({ target: { name: 'role', value: e.target.value } })}
                selected={formData.role}
                error={formError.role}
                style={{ width: '240px' }}
                disabled={editMode === EDIT_MODEL_VIEW}
              />
            </div>
          </div>
          <div className="create-user-form-row">
            <label>Password</label>
            <div className="input-wrapper">
              <CustomInput
                placeholder="Enter your Password"
                type='password'
                name="password"
                error={formError.password}
                value={formData.password}
                onChange={handleInputChange}
                style={{ width: '240px', fontSize: '14px' }}
                disabled={editMode === EDIT_MODEL_VIEW}
              />
              <span className="username-note">Note: Your password must be at least 8 characters, and should contain the following: upper case letters, lower case letters and numbers.</span>
            </div>
          </div>
          <div className="create-user-form-row">
            <label>Confirm Password</label>
            <div className="input-wrapper">
              <CustomInput
                type='password'
                placeholder="Confirm your Password"
                name="confirmPassword"
                error={formError.confirmPassword}
                value={formData.confirmPassword}
                onChange={handleInputChange}
                style={{ width: '240px', fontSize: '14px' }}
                disabled={editMode === EDIT_MODEL_VIEW}
              />
            </div>
          </div>
          {editMode !== EDIT_MODEL_VIEW && <Button className='create-user-btn' onClick={handleSubmit}>SAVE</Button>}
          <Button className='create-user-btn' style={{ marginLeft: '40px' }} onClick={onCancel}>CANCEL</Button>
        </div>
      </div>
      <CommonToast
        show={showToast}
        message={toastMessage}
        onClose={() => setShowToast(false)}
        isError={isError}
      />
    </div>
  );
}

export default CreateUser;